import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Search, Calendar, Clock, CheckCircle, AlertTriangle, Edit, X, Plus, Trash2, Wrench, Battery, Printer, Download, MapPin, KeyRound, ShieldCheck } from 'lucide-react';
import { serviceCallsAPI, clientsAPI, clientFlagsAPI, franklinAPI } from '@shared/apiClient';
import { useAuth } from '@shared/authContext';
import { formatDate, formatDateTime, getStatusColor, getStatusText, getResolutionDisplay, formatCurrency, getClientDisplayName } from '@shared/utils';
import { FlagIcon } from '@shared/flagIcons';
import toast from 'react-hot-toast';
import InventorySearch from '../components/InventorySearch';
import { generateServiceCallPDF, printServiceCallPDF } from '../utils/pdfGenerator';

const ServiceCallEditModal = ({ serviceCall, isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('service'); // 'service' or 'client'
  const [formData, setFormData] = useState({
    workPerformed: '',
    partsUsed: [],
    laborHours: '',
    totalCost: '',
    customerNotes: '',
    status: 'assigned'
  });
  const [clientFormData, setClientFormData] = useState({
    generatorType: '',
    generatorModel: '',
    generatorSerialNumber: '',
    generatorSize: '',
    generatorCoolingType: '',
    generatorExerciseTime: '',
    airFilter: '',
    fuelFilter1: '',
    fuelFilter2: '',
    oilFilter1: '',
    oilFilter2: '',
    fuelAdditive: '',
    fuelType: '',
    oilType: '',
    oilAmount: '',
    batterySize: '',
    batteryDateCode: '',
    batteryDateChanged: '',
    gateCode: false,
    gateCodeValue: ''
  });
  const [initialData, setInitialData] = useState(null);
  const [initialClientData, setInitialClientData] = useState(null);
  const [flagIds, setFlagIds] = useState([]);
  const [initialFlagIds, setInitialFlagIds] = useState([]);
  const [newPart, setNewPart] = useState({ inventoryId: '', itemNumber: '', description: '', name: '', quantity: '', cost: '' });
  const [selectedInventoryItem, setSelectedInventoryItem] = useState(null);
  const [useCustomPart, setUseCustomPart] = useState(false);
  const [searchResetKey, setSearchResetKey] = useState(0);
  const [franklinSiteData, setFranklinSiteData] = useState({});
  const [initialFranklinSiteData, setInitialFranklinSiteData] = useState({});
  const queryClient = useQueryClient();

  const { data: serviceCallDetails } = useQuery(
    ['serviceCallDetails', serviceCall?.id],
    () => serviceCallsAPI.getServiceCall(serviceCall.id),
    { enabled: isOpen && !!serviceCall?.id }
  );

  const { data: flagsData } = useQuery(
    'clientFlags',
    () => clientFlagsAPI.getClientFlags(),
    { enabled: isOpen }
  );

  useEffect(() => {
    const currentServiceCall = serviceCallDetails?.data?.serviceCall || serviceCall;
    if (currentServiceCall) {
      const latestVisit = currentServiceCall.visits?.length
        ? currentServiceCall.visits[currentServiceCall.visits.length - 1]
        : null;
      const normalizePartsUsed = (parts) => {
        if (!parts) return [];
        if (Array.isArray(parts)) return parts;
        if (typeof parts === 'string') {
          try {
            const parsed = JSON.parse(parts);
            return Array.isArray(parsed) ? parsed : [];
          } catch (error) {
            return [];
          }
        }
        return [];
      };
      const workPerformed = currentServiceCall.workPerformed?.trim()
        ? currentServiceCall.workPerformed
        : (latestVisit?.workPerformed || '');
      const partsUsed = normalizePartsUsed(currentServiceCall.partsUsed);
      const visitPartsUsed = normalizePartsUsed(latestVisit?.partsUsed);
      const data = {
        workPerformed,
        partsUsed: partsUsed.length > 0 ? partsUsed : visitPartsUsed,
        laborHours: currentServiceCall.laborHours ?? latestVisit?.laborHours ?? '',
        totalCost: currentServiceCall.totalCost ?? '',
        customerNotes: currentServiceCall.customerNotes || '',
        status: currentServiceCall.status || 'assigned'
      };
      setFormData(data);
      setInitialData(data);

      // Initialize Franklin site data
      if (currentServiceCall.isFranklin && currentServiceCall.franklinSite) {
        const fs = currentServiceCall.franklinSite;
        const fsData = {
          generatorType: fs.generatorType || '',
          generatorModel: fs.generatorModel || '',
          generatorSerialNumber: fs.generatorSerialNumber || '',
          generatorSize: fs.generatorSize || '',
          generatorCoolingType: fs.generatorCoolingType || '',
          generatorExerciseTime: fs.generatorExerciseTime || '',
          airFilter: fs.airFilter || '',
          fuelFilter1: fs.fuelFilter1 || '',
          fuelFilter2: fs.fuelFilter2 || '',
          oilFilter1: fs.oilFilter1 || '',
          oilFilter2: fs.oilFilter2 || '',
          fuelAdditive: fs.fuelAdditive || '',
          fuelType: fs.fuelType || '',
          oilType: fs.oilType || '',
          oilAmount: fs.oilAmount || '',
          batterySize: fs.batterySize || '',
          batteryDateCode: fs.batteryDateCode || ''
        };
        setFranklinSiteData(fsData);
        setInitialFranklinSiteData(fsData);
      }

      // Initialize client data
      if (currentServiceCall.client) {
        const clientData = {
          generatorType: currentServiceCall.client.generatorType || '',
          generatorModel: currentServiceCall.client.generatorModel || '',
          generatorSerialNumber: currentServiceCall.client.generatorSerialNumber || '',
          generatorSize: currentServiceCall.client.generatorSize || '',
          generatorCoolingType: currentServiceCall.client.generatorCoolingType || '',
          generatorExerciseTime: currentServiceCall.client.generatorExerciseTime || '',
          airFilter: currentServiceCall.client.airFilter || '',
          fuelFilter1: currentServiceCall.client.fuelFilter1 || '',
          fuelFilter2: currentServiceCall.client.fuelFilter2 || '',
          oilFilter1: currentServiceCall.client.oilFilter1 || '',
          oilFilter2: currentServiceCall.client.oilFilter2 || '',
          fuelAdditive: currentServiceCall.client.fuelAdditive || '',
          fuelType: currentServiceCall.client.fuelType || '',
          oilType: currentServiceCall.client.oilType || '',
          oilAmount: currentServiceCall.client.oilAmount || '',
          batterySize: currentServiceCall.client.batterySize || '',
          batteryDateCode: currentServiceCall.client.batteryDateCode || '',
          batteryDateChanged: currentServiceCall.client.batteryDateChanged ? new Date(currentServiceCall.client.batteryDateChanged).toISOString().split('T')[0] : '',
          gateCode: currentServiceCall.client.gateCode || false,
          gateCodeValue: currentServiceCall.client.gateCodeValue || ''
        };
        setClientFormData(clientData);
        setInitialClientData(clientData);

        const currentFlagIds = currentServiceCall.client.flags?.map(f => f.id) || [];
        setFlagIds(currentFlagIds);
        setInitialFlagIds(currentFlagIds);
      }
    }
  }, [serviceCall, serviceCallDetails]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  const updateMutation = useMutation(
    (data) => serviceCallsAPI.updateServiceCall(serviceCall.id, data),
    {
      onSuccess: () => {
        toast.success('Service call updated successfully!');
        queryClient.invalidateQueries('myServiceCalls');
        queryClient.invalidateQueries('weeklyAssignments');
        onClose();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update service call');
      }
    }
  );

  const updateClientMutation = useMutation(
    (data) => clientsAPI.updateClientGeneratorInfo(serviceCall.client.id, data),
    {
      onSuccess: () => {
        toast.success('Customer information updated successfully!');
        queryClient.invalidateQueries('myServiceCalls');
        queryClient.invalidateQueries('clients');
        queryClient.invalidateQueries('allClients');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update customer');
      }
    }
  );

  const updateFranklinSiteMutation = useMutation(
    (data) => franklinAPI.updateSiteByTechnician(serviceCall.franklinSiteId, data),
    {
      onSuccess: () => {
        toast.success('Franklin site updated successfully!');
        queryClient.invalidateQueries('myServiceCalls');
        queryClient.invalidateQueries('franklinSites');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update Franklin site');
      }
    }
  );

  const handleInventorySelect = (inventoryItem) => {
    setSelectedInventoryItem(inventoryItem);
    setNewPart({
      inventoryId: inventoryItem.inventoryId || '',
      itemNumber: inventoryItem.itemNumber,
      description: inventoryItem.description,
      name: inventoryItem.itemNumber,
      quantity: inventoryItem.quantity || 1,
      cost: inventoryItem.price || 0
    });
    setUseCustomPart(false); // Switch back to inventory mode when item is selected
  };

  const handleAddPart = () => {
    if (useCustomPart) {
      // Custom part mode - require name, quantity, and cost
      if (newPart.name && newPart.quantity && newPart.cost !== undefined) {
        setFormData({
          ...formData,
          partsUsed: [...formData.partsUsed, {
            itemNumber: newPart.name, // Use name as itemNumber for custom parts
            description: newPart.description || newPart.name,
            name: newPart.name,
            quantity: parseFloat(newPart.quantity),
            cost: parseFloat(newPart.cost)
          }]
        });
        setNewPart({ inventoryId: '', itemNumber: '', description: '', name: '', quantity: '', cost: '' });
        setSelectedInventoryItem(null);
        setSearchResetKey(k => k + 1);
      } else {
        toast.error('Please enter part name, quantity, and cost');
      }
    } else {
      // Inventory mode - require selected item and quantity
      if (newPart.itemNumber && newPart.quantity && newPart.cost !== undefined) {
        setFormData({
          ...formData,
          partsUsed: [...formData.partsUsed, {
            ...(newPart.inventoryId ? { inventoryId: newPart.inventoryId } : {}),
            itemNumber: newPart.itemNumber,
            description: newPart.description || newPart.name,
            name: newPart.itemNumber, // For backward compatibility
            quantity: parseFloat(newPart.quantity),
            cost: parseFloat(newPart.cost)
          }]
        });
        setNewPart({ inventoryId: '', itemNumber: '', description: '', name: '', quantity: '', cost: '' });
        setSelectedInventoryItem(null);
        setSearchResetKey(k => k + 1);
      } else {
        toast.error('Please select a part and enter quantity');
      }
    }
  };

  const handleToggleMode = () => {
    setUseCustomPart(!useCustomPart);
    setSelectedInventoryItem(null);
    setNewPart({ inventoryId: '', itemNumber: '', description: '', name: '', quantity: '', cost: '' });
  };

  const handleRemovePart = (index) => {
    setFormData({
      ...formData,
      partsUsed: formData.partsUsed.filter((_, i) => i !== index)
    });
  };

  const handleClientChange = (e) => {
    const { name, value } = e.target;
    setClientFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (activeTab === 'service') {
      // Only send changed fields
      const updateData = {};
      
      // Compare each field with initial data
      if (formData.workPerformed !== initialData.workPerformed) {
        updateData.workPerformed = formData.workPerformed;
      }
      
      if (JSON.stringify(formData.partsUsed) !== JSON.stringify(initialData.partsUsed)) {
        updateData.partsUsed = formData.partsUsed.length > 0 ? formData.partsUsed : null;
      }
      
      if (formData.laborHours !== initialData.laborHours) {
        updateData.laborHours = formData.laborHours ? parseFloat(formData.laborHours) : null;
      }
      
      if (formData.totalCost !== initialData.totalCost) {
        updateData.totalCost = formData.totalCost ? parseFloat(formData.totalCost) : null;
      }
      
      if (formData.customerNotes !== initialData.customerNotes) {
        updateData.customerNotes = formData.customerNotes;
      }
      
      if (formData.status !== initialData.status) {
        updateData.status = formData.status;
        // If marking as completed, set completedDate
        if (formData.status === 'completed' && serviceCall.status !== 'completed') {
          updateData.completedDate = new Date().toISOString();
        }
      }
      
      // Only submit if there are changes
      if (Object.keys(updateData).length === 0) {
        toast.info('No changes to save');
        return;
      }
      
      updateMutation.mutate(updateData);
    } else if (activeTab === 'client') {
      // Franklin site editing
      if (serviceCall?.isFranklin && serviceCall?.franklinSiteId) {
        const updateData = {};
        Object.keys(franklinSiteData).forEach(key => {
          if (franklinSiteData[key] !== initialFranklinSiteData[key]) {
            updateData[key] = franklinSiteData[key]?.trim() || null;
          }
        });
        if (Object.keys(updateData).length === 0) {
          toast.info('No changes to save');
          return;
        }
        updateFranklinSiteMutation.mutate(updateData);
        return;
      }

      if (!serviceCall?.client?.id) {
        toast.error('No customer record linked to this service call');
        return;
      }

      const updateData = {};
      
      Object.keys(clientFormData).forEach(key => {
        if (clientFormData[key] !== initialClientData[key]) {
          if (typeof clientFormData[key] === 'boolean') {
            updateData[key] = clientFormData[key];
          } else {
            updateData[key] = clientFormData[key]?.trim() || null;
          }
        }
      });

      const flagsChanged = JSON.stringify([...flagIds].sort()) !== JSON.stringify([...initialFlagIds].sort());
      if (flagsChanged) {
        updateData.flagIds = flagIds;
      }
      
      if (Object.keys(updateData).length === 0) {
        toast.info('No changes to save');
        return;
      }
      
      updateClientMutation.mutate(updateData);
    }
  };

  if (!isOpen) return null;

  const client = serviceCall?.client;
  const cspireSite = serviceCall?.cspireWorkOrder?.site;

  const handlePrint = async () => {
    try {
      const response = await serviceCallsAPI.getServiceCall(serviceCall.id);
      const fullServiceCall = response.data.serviceCall;
      await printServiceCallPDF(fullServiceCall);
    } catch (error) {
      toast.error('Failed to generate print preview');
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await serviceCallsAPI.getServiceCall(serviceCall.id);
      const fullServiceCall = response.data.serviceCall;
      await generateServiceCallPDF(fullServiceCall);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to generate PDF');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-white">
      <div className="fixed inset-0 flex h-full w-full flex-col bg-white">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h3 className="text-xl font-semibold text-gray-900">
            {activeTab === 'service' ? 'Edit Service Call' : serviceCall?.isFranklin ? 'Edit Franklin Site' : 'Edit Customer Information'}
          </h3>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={handlePrint}
              className="icon-btn"
              title="Print"
            >
              <Printer className="h-6 w-6" />
            </button>
            <button
              type="button"
              onClick={handleDownloadPDF}
              className="icon-btn"
              title="Download PDF"
            >
              <Download className="h-6 w-6" />
            </button>
            <button
              onClick={onClose}
              className="icon-btn"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 px-6">
          <nav className="flex -mb-px gap-2">
            <button
              onClick={() => setActiveTab('service')}
              className={`py-3 px-5 text-base font-semibold border-b-2 transition-colors ${
                activeTab === 'service'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Edit className="inline h-4 w-4 mr-1" />
              Service Details
            </button>
            {(serviceCall?.client || serviceCall?.isFranklin) && (
            <button
              onClick={() => setActiveTab('client')}
              className={`py-3 px-5 text-base font-semibold border-b-2 transition-colors ${
                activeTab === 'client'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Wrench className="inline h-4 w-4 mr-1" />
              {serviceCall?.isFranklin ? 'Edit Site' : 'Edit Customer'}
            </button>
            )}
          </nav>
        </div>

        <form onSubmit={handleSubmit} className="flex h-full min-h-0 flex-col">
          <div className="flex-1 overflow-y-auto overscroll-contain touch-pan-y px-6 py-6 space-y-5">
          {activeTab === 'service' ? (
            <>
              {/* Client and Generator Info (Read-only) */}
              <div className="bg-gray-50 p-4 rounded-xl space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-base font-semibold text-gray-700">
                    <span className="font-semibold">Customer:</span>{' '}
                    {serviceCall?.isFranklin ? `Franklin - ${serviceCall.franklinSite?.locationName || 'Site'}` : serviceCall?.isCspire ? `CSpire - ${serviceCall.cspireWorkOrder?.locationName || cspireSite?.locationName || 'Site'}` : getClientDisplayName(client)}
                  </p>
                  {serviceCall?.isWarranty && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-300">
                      <ShieldCheck className="h-3 w-3" />
                      Warranty
                    </span>
                  )}
                </div>
                {serviceCall?.isFranklin && serviceCall.franklinSite && (
                  <div className="text-sm text-gray-600 space-y-1">
                    <p><span className="font-semibold">Site:</span> {serviceCall.franklinSite.siteId} ({serviceCall.franklinSite.company})</p>
                    {serviceCall.franklinSite.address && (
                      <p><span className="font-semibold">Address:</span> {serviceCall.franklinSite.address}, {serviceCall.franklinSite.city}, {serviceCall.franklinSite.state}</p>
                    )}
                  </div>
                )}
                {serviceCall?.isCspire && cspireSite && (
                  <div className="text-sm text-gray-600 space-y-1">
                    {(cspireSite.siteId || cspireSite.locationName) && (
                      <p>
                        <span className="font-semibold">Site:</span>{' '}
                        {cspireSite.siteId && `#${cspireSite.siteId}`}
                        {cspireSite.siteId && cspireSite.locationName && ' — '}
                        {cspireSite.locationName}
                      </p>
                    )}
                    {cspireSite.addressLine1 && (
                      <p>
                        <span className="font-semibold">Address:</span>{' '}
                        {cspireSite.addressLine1}{cspireSite.city && `, ${cspireSite.city}`}{cspireSite.state && `, ${cspireSite.state}`} {cspireSite.zipCode}
                      </p>
                    )}
                    {serviceCall.cspireWorkOrder?.workOrderNumber && (
                      <p><span className="font-semibold">WO#:</span> {serviceCall.cspireWorkOrder.workOrderNumber}</p>
                    )}
                  </div>
                )}
                {client && (client.generatorModel || client.generatorSerialNumber) && (
                  <p className="text-sm text-gray-600">
                    <span className="font-semibold">Generator:</span> {client.generatorModel || ''} {client.generatorSerialNumber ? `(${client.generatorSerialNumber})` : ''}
                  </p>
                )}
                {client?.gateCode && client?.gateCodeValue && (
                  <div className="flex items-center gap-2 mt-1 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    <KeyRound className="h-4 w-4 text-amber-600 flex-shrink-0" />
                    <span className="text-sm font-semibold text-amber-900">Gate Code: {client.gateCodeValue}</span>
                  </div>
                )}
              </div>

          {/* Work Performed */}
          <div>
            <label className="label">
              Work Performed
            </label>
            <textarea
              value={formData.workPerformed}
              onChange={(e) => setFormData({ ...formData, workPerformed: e.target.value })}
              rows={4}
              className="input text-lg"
              placeholder="Describe the work performed..."
            />
          </div>

          {/* Parts Used */}
          <div>
            <label className="label">
              Parts Used
            </label>
            {formData.partsUsed.length > 0 && (
              <div className="mb-3 space-y-3">
                {formData.partsUsed.map((part, index) => {
                  const itemNumber = part.itemNumber || part.name || 'N/A';
                  const description = part.description || part.name || '';
                  const quantity = parseFloat(part.quantity) || 0;
                  const cost = parseFloat(part.cost) || 0;
                  return (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-xl gap-3">
                      <div className="flex-1">
                        <div className="text-base font-medium text-gray-900">{itemNumber}</div>
                        {description && <div className="text-sm text-gray-600">{description}</div>}
                        <div className="text-sm text-gray-500 mt-1">
                          Qty: {quantity.toFixed(2)} @ {formatCurrency(cost)} = {formatCurrency(quantity * cost)}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemovePart(index)}
                        className="icon-btn-danger flex-shrink-0"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="space-y-4">
              {/* Toggle between inventory search and custom part */}
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">
                  {useCustomPart ? 'Custom Part Mode' : 'Inventory Search Mode'}
                </span>
                <button
                  type="button"
                  onClick={handleToggleMode}
                  className="link-btn"
                >
                  {useCustomPart ? 'Search Inventory Instead' : 'Add Custom Part Instead'}
                </button>
              </div>

              {useCustomPart ? (
                // Custom part entry mode
                <div className="space-y-4">
                  <div>
                    <label className="label">Part Name/Number *</label>
                    <input
                      type="text"
                      placeholder="Enter part name or number"
                      value={newPart.name}
                      onChange={(e) => setNewPart({ ...newPart, name: e.target.value })}
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="label">Description (optional)</label>
                    <input
                      type="text"
                      placeholder="Enter part description"
                      value={newPart.description}
                      onChange={(e) => setNewPart({ ...newPart, description: e.target.value })}
                      className="input"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="label">Quantity *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        placeholder="1.00"
                        value={newPart.quantity}
                        onChange={(e) => setNewPart({ ...newPart, quantity: e.target.value })}
                        className="input"
                      />
                    </div>
                    <div>
                      <label className="label">Cost *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={newPart.cost}
                        onChange={(e) => setNewPart({ ...newPart, cost: e.target.value })}
                        className="input"
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={handleAddPart}
                        disabled={!newPart.name || !newPart.quantity || !newPart.cost}
                        className="btn btn-secondary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Plus className="h-5 w-5 mr-1" />
                        Add Part
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                // Inventory search mode
                <>
                  <InventorySearch
                    onSelect={handleInventorySelect}
                    placeholder="Search for parts by item number or description..."
                    resetKey={searchResetKey}
                  />
                  {selectedInventoryItem && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-base">
                      <div className="font-medium text-blue-900">{selectedInventoryItem.itemNumber}</div>
                      <div className="text-blue-700">{selectedInventoryItem.description}</div>
                      <div className="text-blue-600 mt-1">Price: ${parseFloat(selectedInventoryItem.price || selectedInventoryItem.cost).toFixed(2)}</div>
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="label">Quantity</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        placeholder="1.00"
                        value={newPart.quantity}
                        onChange={(e) => setNewPart({ ...newPart, quantity: e.target.value })}
                        className="input"
                      />
                    </div>
                    <div>
                      <label className="label">Price (override)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder={selectedInventoryItem ? `$${parseFloat(selectedInventoryItem.price || selectedInventoryItem.cost).toFixed(2)}` : "Price"}
                        value={newPart.cost}
                        onChange={(e) => setNewPart({ ...newPart, cost: e.target.value })}
                        className="input"
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={handleAddPart}
                        disabled={!selectedInventoryItem || !newPart.quantity}
                        className="btn btn-secondary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Plus className="h-5 w-5 mr-1" />
                        Add Part
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Labor Hours and Total Cost */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">
                Labor Hours
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.laborHours}
                onChange={(e) => setFormData({ ...formData, laborHours: e.target.value })}
                className="input"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="label">
                Total Cost
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.totalCost}
                onChange={(e) => setFormData({ ...formData, totalCost: e.target.value })}
                className="input"
                placeholder="0.00"
              />
            </div>
          </div>

          {/* Customer Notes */}
          <div>
            <label className="label">
              Customer Notes
            </label>
            <textarea
              value={formData.customerNotes}
              onChange={(e) => setFormData({ ...formData, customerNotes: e.target.value })}
              rows={3}
              className="input"
              placeholder="Notes for the customer..."
            />
          </div>

          {/* Status */}
          <div>
            <label className="label">
              Status
            </label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="select"
            >
              <option value="assigned">Assigned</option>
              <option value="scheduled">Scheduled</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="return">Return</option>
              <option value="cancelled">Cancelled</option>
              <option value="rescheduled">Rescheduled</option>
            </select>
          </div>
            </>
          ) : serviceCall?.isFranklin ? (
            <>
              {/* Franklin Site Editing Form */}
              <div className="space-y-5">
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
                  <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center">
                    <Wrench className="h-5 w-5 mr-2 text-emerald-600" />
                    Generator / Equipment
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="label">Type</label>
                      <input type="text" value={franklinSiteData.generatorType || ''} onChange={(e) => setFranklinSiteData(prev => ({ ...prev, generatorType: e.target.value }))} className="input" placeholder="e.g., Standby" />
                    </div>
                    <div>
                      <label className="label">Model</label>
                      <input type="text" value={franklinSiteData.generatorModel || ''} onChange={(e) => setFranklinSiteData(prev => ({ ...prev, generatorModel: e.target.value }))} className="input" placeholder="Model number" />
                    </div>
                    <div>
                      <label className="label">Serial Number</label>
                      <input type="text" value={franklinSiteData.generatorSerialNumber || ''} onChange={(e) => setFranklinSiteData(prev => ({ ...prev, generatorSerialNumber: e.target.value }))} className="input" placeholder="Serial number" />
                    </div>
                    <div>
                      <label className="label">Size</label>
                      <input type="text" value={franklinSiteData.generatorSize || ''} onChange={(e) => setFranklinSiteData(prev => ({ ...prev, generatorSize: e.target.value }))} className="input" placeholder="e.g., 50kW" />
                    </div>
                    <div>
                      <label className="label">Cooling Type</label>
                      <select value={franklinSiteData.generatorCoolingType || ''} onChange={(e) => setFranklinSiteData(prev => ({ ...prev, generatorCoolingType: e.target.value }))} className="input">
                        <option value="">Unknown</option>
                        <option value="air">Air-cooled</option>
                        <option value="water">Water-cooled</option>
                      </select>
                    </div>
                    <div className="col-span-2">
                      <label className="label">Exercise Time</label>
                      <input type="text" value={franklinSiteData.generatorExerciseTime || ''} onChange={(e) => setFranklinSiteData(prev => ({ ...prev, generatorExerciseTime: e.target.value }))} className="input" placeholder="e.g., 2:00 PM" />
                    </div>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                  <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center">
                    <Wrench className="h-5 w-5 mr-2 text-amber-600" />
                    Filters & Fluids
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="label">Air Filter</label>
                      <input type="text" value={franklinSiteData.airFilter || ''} onChange={(e) => setFranklinSiteData(prev => ({ ...prev, airFilter: e.target.value }))} className="input" placeholder="Part number" />
                    </div>
                    <div>
                      <label className="label">Fuel Filter 1</label>
                      <input type="text" value={franklinSiteData.fuelFilter1 || ''} onChange={(e) => setFranklinSiteData(prev => ({ ...prev, fuelFilter1: e.target.value }))} className="input" placeholder="Part number" />
                    </div>
                    <div>
                      <label className="label">Fuel Filter 2</label>
                      <input type="text" value={franklinSiteData.fuelFilter2 || ''} onChange={(e) => setFranklinSiteData(prev => ({ ...prev, fuelFilter2: e.target.value }))} className="input" placeholder="Part number" />
                    </div>
                    <div>
                      <label className="label">Oil Filter 1</label>
                      <input type="text" value={franklinSiteData.oilFilter1 || ''} onChange={(e) => setFranklinSiteData(prev => ({ ...prev, oilFilter1: e.target.value }))} className="input" placeholder="Part number" />
                    </div>
                    <div>
                      <label className="label">Oil Filter 2</label>
                      <input type="text" value={franklinSiteData.oilFilter2 || ''} onChange={(e) => setFranklinSiteData(prev => ({ ...prev, oilFilter2: e.target.value }))} className="input" placeholder="Part number" />
                    </div>
                    <div>
                      <label className="label">Fuel Type</label>
                      <input type="text" value={franklinSiteData.fuelType || ''} onChange={(e) => setFranklinSiteData(prev => ({ ...prev, fuelType: e.target.value }))} className="input" placeholder="Fuel type" />
                    </div>
                    <div>
                      <label className="label">Fuel Additive</label>
                      <input type="text" value={franklinSiteData.fuelAdditive || ''} onChange={(e) => setFranklinSiteData(prev => ({ ...prev, fuelAdditive: e.target.value }))} className="input" placeholder="Additive" />
                    </div>
                    <div>
                      <label className="label">Oil Type</label>
                      <input type="text" value={franklinSiteData.oilType || ''} onChange={(e) => setFranklinSiteData(prev => ({ ...prev, oilType: e.target.value }))} className="input" placeholder="Oil type" />
                    </div>
                    <div>
                      <label className="label">Oil Amount</label>
                      <input type="text" value={franklinSiteData.oilAmount || ''} onChange={(e) => setFranklinSiteData(prev => ({ ...prev, oilAmount: e.target.value }))} className="input" placeholder="Amount" />
                    </div>
                    <div>
                      <label className="label">Battery Size</label>
                      <input type="text" value={franklinSiteData.batterySize || ''} onChange={(e) => setFranklinSiteData(prev => ({ ...prev, batterySize: e.target.value }))} className="input" placeholder="Battery size" />
                    </div>
                    <div>
                      <label className="label">Battery Date Code</label>
                      <input type="text" value={franklinSiteData.batteryDateCode || ''} onChange={(e) => setFranklinSiteData(prev => ({ ...prev, batteryDateCode: e.target.value }))} className="input" placeholder="Date code" />
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Client Editing Form */}
              <div className="space-y-5">
                {/* Generator Information */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
                  <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center">
                    <Wrench className="h-5 w-5 mr-2 text-blue-600" />
                    Generator Information
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="label">Type</label>
                      <input
                        type="text"
                        name="generatorType"
                        value={clientFormData.generatorType}
                        onChange={handleClientChange}
                        className="input"
                        placeholder="e.g., Standby"
                      />
                    </div>
                    <div>
                      <label className="label">Model</label>
                      <input
                        type="text"
                        name="generatorModel"
                        value={clientFormData.generatorModel}
                        onChange={handleClientChange}
                        className="input"
                        placeholder="Model number"
                      />
                    </div>
                    <div>
                      <label className="label">Serial Number</label>
                      <input
                        type="text"
                        name="generatorSerialNumber"
                        value={clientFormData.generatorSerialNumber}
                        onChange={handleClientChange}
                        className="input"
                        placeholder="Serial number"
                      />
                    </div>
                    <div>
                      <label className="label">Size</label>
                      <input
                        type="text"
                        name="generatorSize"
                        value={clientFormData.generatorSize}
                        onChange={handleClientChange}
                        className="input"
                        placeholder="e.g., 50kW"
                      />
                    </div>
                    <div>
                      <label className="label">Cooling Type</label>
                      <select
                        name="generatorCoolingType"
                        value={clientFormData.generatorCoolingType}
                        onChange={handleClientChange}
                        className="input"
                      >
                        <option value="">Unknown</option>
                        <option value="air">Air-cooled</option>
                        <option value="water">Water-cooled</option>
                      </select>
                    </div>
                    <div className="col-span-2">
                      <label className="label">Exercise Time</label>
                      <input
                        type="text"
                        name="generatorExerciseTime"
                        value={clientFormData.generatorExerciseTime}
                        onChange={handleClientChange}
                        className="input"
                        placeholder="e.g., 2:00 PM"
                      />
                    </div>
                  </div>
                </div>

                {/* Gate / Access Code */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                  <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center">
                    <KeyRound className="h-5 w-5 mr-2 text-amber-600" />
                    Gate / Access Code
                  </h4>
                  <div className="space-y-4">
                    <label className="flex items-center gap-3 cursor-pointer py-2">
                      <div className="relative inline-flex items-center">
                        <input
                          type="checkbox"
                          checked={clientFormData.gateCode}
                          onChange={(e) => setClientFormData(prev => ({
                            ...prev,
                            gateCode: e.target.checked,
                            gateCodeValue: e.target.checked ? prev.gateCodeValue : ''
                          }))}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                      </div>
                      <span className="text-base font-medium text-gray-700">Gate code required</span>
                    </label>
                    {clientFormData.gateCode && (
                      <div>
                        <label className="label">Gate Code</label>
                        <input
                          type="text"
                          name="gateCodeValue"
                          value={clientFormData.gateCodeValue}
                          onChange={handleClientChange}
                          className="input"
                          placeholder="Enter gate code or access instructions"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Customer Flags */}
                {flagsData?.data?.flags?.length > 0 && (
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
                    <h4 className="text-base font-semibold text-gray-900 mb-4">Customer Flags</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {flagsData.data.flags.filter(f => f.isActive).map((flag) => {
                        const isSelected = flagIds.includes(flag.id);
                        return (
                          <label
                            key={flag.id}
                            className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-base cursor-pointer transition-colors active:scale-[0.98] ${
                              isSelected
                                ? 'border-blue-400 bg-blue-50 text-blue-700'
                                : 'border-gray-200 text-gray-700 hover:border-blue-300'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => {
                                setFlagIds(prev =>
                                  isSelected ? prev.filter(id => id !== flag.id) : [...prev, flag.id]
                                );
                              }}
                              className="checkbox"
                            />
                            <FlagIcon iconName={flag.icon} className="h-5 w-5" title={flag.name} />
                            <span>{flag.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Filters & Fluids */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                  <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center">
                    <Wrench className="h-5 w-5 mr-2 text-amber-600" />
                    Filters & Fluids
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="label">Air Filter</label>
                      <input
                        type="text"
                        name="airFilter"
                        value={clientFormData.airFilter}
                        onChange={handleClientChange}
                        className="input"
                        placeholder="Part number"
                      />
                    </div>
                    <div>
                      <label className="label">Fuel Filter 1</label>
                      <input
                        type="text"
                        name="fuelFilter1"
                        value={clientFormData.fuelFilter1}
                        onChange={handleClientChange}
                        className="input"
                        placeholder="Part number"
                      />
                    </div>
                    <div>
                      <label className="label">Fuel Filter 2</label>
                      <input
                        type="text"
                        name="fuelFilter2"
                        value={clientFormData.fuelFilter2}
                        onChange={handleClientChange}
                        className="input"
                        placeholder="Part number"
                      />
                    </div>
                    <div>
                      <label className="label">Oil Filter 1</label>
                      <input
                        type="text"
                        name="oilFilter1"
                        value={clientFormData.oilFilter1}
                        onChange={handleClientChange}
                        className="input"
                        placeholder="Part number"
                      />
                    </div>
                    <div>
                      <label className="label">Oil Filter 2</label>
                      <input
                        type="text"
                        name="oilFilter2"
                        value={clientFormData.oilFilter2}
                        onChange={handleClientChange}
                        className="input"
                        placeholder="Part number"
                      />
                    </div>
                    <div>
                      <label className="label">Fuel Additive</label>
                      <input
                        type="text"
                        name="fuelAdditive"
                        value={clientFormData.fuelAdditive}
                        onChange={handleClientChange}
                        className="input"
                        placeholder="Type or brand"
                      />
                    </div>
                    <div>
                      <label className="label">Fuel Type</label>
                      <input
                        type="text"
                        name="fuelType"
                        value={clientFormData.fuelType}
                        onChange={handleClientChange}
                        className="input"
                        placeholder="e.g., Diesel"
                      />
                    </div>
                    <div>
                      <label className="label">Oil Type</label>
                      <input
                        type="text"
                        name="oilType"
                        value={clientFormData.oilType}
                        onChange={handleClientChange}
                        className="input"
                        placeholder="e.g., 15W-40"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="label">Oil Amount</label>
                      <input
                        type="text"
                        name="oilAmount"
                        value={clientFormData.oilAmount}
                        onChange={handleClientChange}
                        className="input"
                        placeholder="e.g., 5 quarts"
                      />
                    </div>
                  </div>
                </div>

                {/* Battery Information */}
                <div className="bg-green-50 border border-green-200 rounded-xl p-5">
                  <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center">
                    <Battery className="h-5 w-5 mr-2 text-green-600" />
                    Battery Information
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="label">Battery Size</label>
                      <input
                        type="text"
                        name="batterySize"
                        value={clientFormData.batterySize}
                        onChange={handleClientChange}
                        className="input"
                        placeholder="e.g., Group 24"
                      />
                    </div>
                    <div>
                      <label className="label">Date Code</label>
                      <input
                        type="text"
                        name="batteryDateCode"
                        value={clientFormData.batteryDateCode}
                        onChange={handleClientChange}
                        maxLength={5}
                        className="input"
                        placeholder="MM/YY"
                      />
                    </div>
                    <div>
                      <label className="label">Date Changed</label>
                      <input
                        type="date"
                        name="batteryDateChanged"
                        value={clientFormData.batteryDateChanged}
                        onChange={handleClientChange}
                        className="input"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
          
          </div>
          <div className="flex flex-wrap justify-end gap-3 border-t border-slate-200 bg-white px-6 py-4">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateMutation.isLoading || updateClientMutation.isLoading}
              className="btn btn-primary disabled:opacity-50"
            >
              {(updateMutation.isLoading || updateClientMutation.isLoading) ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default function MyServiceCalls() {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedServiceCall, setSelectedServiceCall] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery(
    ['myServiceCalls', page, searchTerm, statusFilter],
    () => serviceCallsAPI.getServiceCalls({ 
      technicianId: user?.id,
      page, 
      limit: 20, 
      search: searchTerm,
      status: statusFilter || undefined,
      sortBy: 'completedDate',
      sortOrder: 'DESC'
    }),
    { keepPreviousData: true }
  );

  const updateServiceCallMutation = useMutation(
    ({ id, data }) => serviceCallsAPI.updateServiceCall(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('myServiceCalls');
        queryClient.invalidateQueries('todaysServiceCalls');
        queryClient.invalidateQueries('upcomingServiceCalls');
        queryClient.invalidateQueries('weeklyAssignments');
        toast.success('Service call updated successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update service call');
      },
    }
  );

  const handleStatusChange = (serviceCallId, newStatus) => {
    const updateData = { status: newStatus };
    if (newStatus === 'completed') {
      updateData.completedDate = new Date().toISOString();
    }
    updateServiceCallMutation.mutate({
      id: serviceCallId,
      data: updateData
    });
  };

  const handleEdit = (serviceCall) => {
    setSelectedServiceCall(serviceCall);
    setIsEditModalOpen(true);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'in_progress':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'scheduled':
        return <Calendar className="h-4 w-4 text-blue-500" />;
      case 'assigned':
        return <AlertTriangle className="h-4 w-4 text-blue-500" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-gray-500" />;
    }
  };

const formatCspireCoords = (site) => {
  if (!site) return null;
  const { latitude, longitude } = site;
  if (latitude === null || latitude === undefined || longitude === null || longitude === undefined) return null;
  const lat = typeof latitude === 'number' ? latitude.toFixed(6) : latitude;
  const lon = typeof longitude === 'number' ? longitude.toFixed(6) : longitude;
  if (lat === '' || lon === '') return null;
  return `${lat}, ${lon}`;
};

const getCspireMapUrl = (site) => {
  if (!site) return null;
  const { latitude, longitude } = site;
  if (latitude === null || latitude === undefined || longitude === null || longitude === undefined) return null;
  if (latitude === '' || longitude === '') return null;
  return `https://maps.google.com/?q=${latitude},${longitude}`;
};

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">My Service Calls</h1>
          <p className="mt-1 text-base text-slate-500">
            Manage your assigned service calls
          </p>
        </div>
        <div className="text-sm font-semibold text-slate-500">
          Tap a card to edit or update status
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-[2fr_1fr]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search service calls..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-11"
              />
            </div>
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="input"
              >
                <option value="">All Statuses</option>
                <option value="assigned">Assigned</option>
                <option value="scheduled">Scheduled</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
                <option value="rescheduled">Rescheduled</option>
                <option value="return">Return</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Service Calls List */}
      <div className="space-y-5">
        {data?.data?.serviceCalls?.map((serviceCall) => {
          const client = serviceCall.client;
          const cspireSite = serviceCall.cspireWorkOrder?.site;
          const franklinSite = serviceCall.franklinSite;
          const cspireCoords = formatCspireCoords(cspireSite);
          const cspireMapUrl = getCspireMapUrl(cspireSite);
          const displayDate = serviceCall.completedDate || null;
          const dateObj = displayDate ? new Date(displayDate) : null;
          
          return (
            <div key={serviceCall.id} className="card">
              <div className="card-body">
                <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:gap-6">
                  {/* Calendar Date Display */}
                  <div className="flex-shrink-0">
                    {displayDate ? (
                      <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-4 text-center w-24">
                        <div className="text-3xl font-bold text-blue-900 leading-none">
                          {dateObj.getDate()}
                        </div>
                        <div className="text-xs text-blue-600 mt-1 font-semibold uppercase tracking-wide">
                          {dateObj.toLocaleString('en-US', { month: 'short' })} {dateObj.getFullYear()}
                        </div>
                      </div>
                    ) : (
                      <div className="bg-gray-50 border-2 border-gray-200 rounded-2xl p-4 text-center w-24">
                        <div className="text-xs text-gray-500 font-semibold uppercase tracking-wide">
                          Not
                        </div>
                        <div className="text-xs text-gray-500 font-semibold uppercase tracking-wide">
                          Completed
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0 space-y-3">
                    <div className="flex flex-wrap items-center gap-3">
                      {getStatusIcon(serviceCall.status)}
                      <h3 className="text-lg font-semibold text-gray-900">
                        {serviceCall?.isFranklin ? `Franklin - ${franklinSite?.locationName || franklinSite?.siteId || 'Site'}` : serviceCall?.isCspire ? `CSpire - ${serviceCall.cspireWorkOrder?.locationName || cspireSite?.locationName || 'Site'}` : getClientDisplayName(client)}
                      </h3>
                      {(() => {
                        const rd = getResolutionDisplay(serviceCall);
                        if (rd) {
                          return <span className={`status-badge ${rd.color}`}>{rd.label}</span>;
                        }
                        return (
                          <span className={`status-badge ${getStatusColor(serviceCall.status)}`}>
                            {getStatusText(serviceCall.status)}
                          </span>
                        );
                      })()}
                      {serviceCall.isWarranty && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-300">
                          <ShieldCheck className="h-3 w-3" />
                          Warranty
                        </span>
                      )}
                    </div>

                    {/* C Spire Site Details */}
                    {serviceCall.isCspire && cspireSite && (
                      <div className="rounded-lg bg-indigo-50 border border-indigo-200 p-3 space-y-1">
                        <div className="flex items-center gap-2 text-sm font-semibold text-indigo-900">
                          <MapPin className="h-4 w-4 text-indigo-600" />
                          {cspireSite.siteId && `Site #${cspireSite.siteId}`}
                          {cspireSite.siteId && cspireSite.locationName && ' — '}
                          {cspireSite.locationName || ''}
                        </div>
                        {cspireSite.addressLine1 && (
                          <p className="text-sm text-indigo-700 ml-6">
                            {cspireSite.addressLine1}{cspireSite.city && `, ${cspireSite.city}`}{cspireSite.state && `, ${cspireSite.state}`} {cspireSite.zipCode}
                          </p>
                        )}
                        {cspireCoords && (
                          <p className="text-sm text-indigo-700 ml-6">
                            <a href={cspireMapUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                              {cspireCoords} (Map)
                            </a>
                          </p>
                        )}
                        {serviceCall.cspireWorkOrder?.workOrderNumber && (
                          <p className="text-sm text-indigo-700 ml-6">
                            <strong>WO#:</strong> {serviceCall.cspireWorkOrder.workOrderNumber}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Franklin Site Details */}
                    {serviceCall.isFranklin && franklinSite && (
                      <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 space-y-1">
                        <div className="flex items-center gap-2 text-sm font-semibold text-emerald-900">
                          <MapPin className="h-4 w-4 text-emerald-600" />
                          {franklinSite.siteId}
                          {franklinSite.locationName && ` — ${franklinSite.locationName}`}
                        </div>
                        <p className="text-sm text-emerald-700 ml-6">
                          <strong>Company:</strong> {franklinSite.company}
                        </p>
                        {franklinSite.address && (
                          <p className="text-sm text-emerald-700 ml-6">
                            {franklinSite.address}{franklinSite.city && `, ${franklinSite.city}`}{franklinSite.state && `, ${franklinSite.state}`} {franklinSite.zipCode}
                          </p>
                        )}
                        {franklinSite.backupPower && (
                          <p className="text-sm text-emerald-700 ml-6">
                            <strong>Backup Power:</strong> {franklinSite.backupPower}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Gate Code Callout */}
                    {client?.gateCode && client?.gateCodeValue && (
                      <div className="flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
                        <KeyRound className="h-4 w-4 text-amber-600 flex-shrink-0" />
                        <span className="text-sm font-semibold text-amber-900">Gate Code: {client.gateCodeValue}</span>
                      </div>
                    )}

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {client?.isCommercial && client?.companyName && (
                        <div className="text-sm text-gray-500">
                          <strong>Company:</strong> {client.companyName}
                        </div>
                      )}
                      {serviceCall.scheduledDate && (
                        <div className="text-sm text-gray-500">
                          <strong>Scheduled:</strong> {formatDate(serviceCall.scheduledDate)}
                        </div>
                      )}
                      {serviceCall.client && (serviceCall.client.generatorModel || serviceCall.client.generatorSerialNumber) && (
                        <div className="text-sm text-gray-500">
                          <strong>Generator:</strong> {serviceCall.client.generatorModel || ''} {serviceCall.client.generatorSerialNumber ? `(${serviceCall.client.generatorSerialNumber})` : ''}
                        </div>
                      )}
                      {serviceCall.isCspire && cspireSite?.generatorType && (
                        <div className="text-sm text-gray-500">
                          <strong>Generator:</strong> {cspireSite.generatorType} {cspireSite.generatorModel && `- ${cspireSite.generatorModel}`} {cspireSite.generatorSerialNumber && `(${cspireSite.generatorSerialNumber})`}
                        </div>
                      )}
                    </div>

                    {serviceCall.description && (
                      <p className="text-base text-gray-600">{serviceCall.description}</p>
                    )}

                    {serviceCall.workPerformed && (
                      <div className="rounded-xl bg-gray-50 p-4">
                        <p className="text-base font-semibold text-gray-700">Work Performed:</p>
                        <p className="text-base text-gray-600">{serviceCall.workPerformed}</p>
                      </div>
                    )}

                    {serviceCall.partsUsed && serviceCall.partsUsed.length > 0 && (
                      <div className="rounded-xl bg-gray-50 p-4">
                        <p className="text-sm font-semibold text-gray-700 mb-2">Parts Used:</p>
                        <div className="space-y-2">
                          {serviceCall.partsUsed.map((part, idx) => {
                            const itemNumber = part.itemNumber || part.name || 'N/A';
                            const description = part.description || part.name || '';
                            const quantity = parseFloat(part.quantity) || 0;
                            const cost = parseFloat(part.cost) || 0;
                            return (
                              <div key={idx} className="text-sm text-gray-600 border-b border-gray-200 pb-2">
                                <div className="font-semibold text-gray-900">{itemNumber}</div>
                                {description && <div className="text-xs text-gray-500">{description}</div>}
                                <div className="text-xs mt-1">
                                  Qty: {quantity.toFixed(2)} @ {formatCurrency(cost)} = {formatCurrency(quantity * cost)}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {(serviceCall.laborHours || serviceCall.totalCost) && (
                      <div className="text-sm text-gray-600">
                        {serviceCall.laborHours && <span><strong>Labor Hours:</strong> {serviceCall.laborHours} </span>}
                        {serviceCall.totalCost && <span><strong>Total Cost:</strong> {formatCurrency(serviceCall.totalCost)}</span>}
                      </div>
                    )}

                    {serviceCall.customerNotes && (
                      <div className="rounded-xl bg-blue-50 p-4">
                        <p className="text-sm font-semibold text-gray-700">Customer Notes:</p>
                        <p className="text-sm text-gray-600">{serviceCall.customerNotes}</p>
                      </div>
                    )}
                  </div>

                  <div className="flex-shrink-0">
                    <div className="flex flex-row flex-wrap gap-2 lg:flex-col">
                      {serviceCall.status === 'scheduled' && (
                        <button
                          onClick={() => handleStatusChange(serviceCall.id, 'in_progress')}
                          className="btn btn-primary btn-sm"
                          disabled={updateServiceCallMutation.isLoading}
                        >
                          Start Work
                        </button>
                      )}
                      {serviceCall.status === 'in_progress' && (
                        <button
                          onClick={() => handleStatusChange(serviceCall.id, 'completed')}
                          className="btn btn-primary btn-sm"
                          disabled={updateServiceCallMutation.isLoading}
                        >
                          Mark Complete
                        </button>
                      )}
                      <button 
                        onClick={() => handleEdit(serviceCall)}
                        className="btn btn-secondary btn-sm"
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            const response = await serviceCallsAPI.getServiceCall(serviceCall.id);
                            await generateServiceCallPDF(response.data.serviceCall);
                            toast.success('PDF downloaded');
                          } catch (error) {
                            toast.error('Failed to generate PDF');
                          }
                        }}
                        className="btn btn-secondary btn-sm"
                      >
                        <Printer className="h-4 w-4 mr-1" />
                        Print
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {data?.data?.serviceCalls?.length === 0 && (
          <div className="text-center py-12">
            <Calendar className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No service calls</h3>
            <p className="mt-1 text-sm text-gray-500">
              You don't have any service calls assigned yet.
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {data?.data?.pagination && (
        <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm font-medium text-gray-700">
            Showing {data.data.pagination.start} to {data.data.pagination.end} of{' '}
            {data.data.pagination.total} results
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setPage(page - 1)}
              disabled={!data.data.pagination.hasPrev}
              className="btn btn-secondary btn-sm disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(page + 1)}
              disabled={!data.data.pagination.hasNext}
              className="btn btn-secondary btn-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {selectedServiceCall && (
        <ServiceCallEditModal
          serviceCall={selectedServiceCall}
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedServiceCall(null);
          }}
        />
      )}
    </div>
  );
}
